/**
 * Cold start — multi-device sync Part B
 * (app/plans/01_08_2026/vault-multidevice/PLAN.md §B1-§B3, §7).
 *
 * A device with nothing local yet (no `vault_meta` row for this scope)
 * deriving the DEK from a passphrase/recovery phrase plus the server-
 * supplied keyblock, rather than running the fresh-vault bootstrap ceremony
 * over an account that already has one.
 */
import { invoke } from '@tauri-apps/api/core';
import { ensureVaultSync } from './vaultSync';

/**
 * Read-only, cheap, silent — no passphrase prompt is shown from this call
 * alone. Returns `{ decision: 'not_found'|'found'|'conflict'|'offline'|'update_required',
 * vaultId }`.
 */
export async function probeColdStart() {
  return invoke('vault_cold_start_probe');
}

/**
 * Cold-starts this device onto the signed-in account's existing vault.
 * Resolves to a `VaultStatus`, or throws a mapped token (`offline`,
 * `vault_kdf_unsupported`, `vault_kdf_corrupt`, `bad_passphrase`,
 * `vault_id_conflict`, `vault_locked`, `vault_meta_missing`).
 */
export async function unlockColdStart(secret) {
  return invoke('vault_cold_start_unlock', { secret });
}

/**
 * Only called when the install decision was Fresh/AdoptIntoExisting, never
 * on a fall-through to the ordinary gate. Reuses `ensureVaultSync` — the
 * moment cold start's install commits, the vault is immediately eligible to
 * enrol (§A1), so this is the exact same enrol/register call every other
 * unlock already makes, not a duplicate.
 */
export async function finishColdStart() {
  await ensureVaultSync();
}
